import type { ComplianceData } from './evaluate.js';
import type { AreaCodeInfo, AreaCodeRegion } from './location.js';
import type { OverrideRow, StateRuleRow } from './rules.js';
import type { MessageClass } from './window.js';

/**
 * Raw reference rows (as Postgres returns them, snake_case) → the compliance data the window code uses.
 * Pure. The worker (Kysely) and the web app (supabase-js, through RLS) both read the same three platform
 * tables and the workspace's overrides, and map them here, so both sides decide with the same data.
 */

export interface StateRuleDbRow {
  state: string;
  name: string;
  timezones: readonly string[];
  allowed_start: string | null;
  allowed_end: string | null;
  sunday_allowed: boolean;
  sunday_start: string | null;
  max_per_24h: number | null;
  cap_applies_to: readonly string[] | string;
  verified_by_counsel: boolean;
  source_url: string | null;
  notes: string | null;
}

export interface AreaCodeDbRow {
  area_code: string;
  region: AreaCodeRegion;
  state: string | null;
  timezone: string | null;
}

export interface OverrideDbRow {
  state: string | null;
  allowed_start: string | null;
  allowed_end: string | null;
  sunday_allowed: boolean;
  max_per_24h: number | null;
  cap_applies_to: readonly string[] | string;
}

/** msg_class[] arrives as a JS array (PostgREST) or as "{campaign,reply}" (node-postgres). */
export function parseClasses(raw: readonly string[] | string): MessageClass[] {
  const list = typeof raw === 'string' ? raw.replace(/^\{|\}$/g, '').split(',') : raw;
  return list.filter((c): c is MessageClass => c === 'campaign' || c === 'reply');
}

export function toStateRule(s: StateRuleDbRow): StateRuleRow {
  return {
    state: s.state,
    name: s.name,
    timezones: s.timezones,
    allowedStart: s.allowed_start,
    allowedEnd: s.allowed_end,
    sundayAllowed: s.sunday_allowed,
    sundayStart: s.sunday_start,
    maxPer24h: s.max_per_24h,
    capAppliesTo: parseClasses(s.cap_applies_to),
    verifiedByCounsel: s.verified_by_counsel,
    sourceUrl: s.source_url,
    notes: s.notes,
  };
}

export function toOverride(o: OverrideDbRow): OverrideRow {
  return {
    state: o.state,
    allowedStart: o.allowed_start,
    allowedEnd: o.allowed_end,
    sundayAllowed: o.sunday_allowed,
    maxPer24h: o.max_per_24h,
    capAppliesTo: parseClasses(o.cap_applies_to),
  };
}

export function toComplianceData(rows: {
  states: readonly StateRuleDbRow[];
  holidays: readonly { holiday_date: string }[];
  areaCodes: readonly AreaCodeDbRow[];
}): ComplianceData {
  const stateRules = new Map(rows.states.map((s) => [s.state, toStateRule(s)]));
  return {
    stateRules,
    stateZones: new Map(rows.states.map((s) => [s.state, s.timezones])),
    closedDates: new Set(rows.holidays.map((h) => h.holiday_date)),
    areaCodes: new Map<string, AreaCodeInfo>(
      rows.areaCodes.map((c) => [
        c.area_code,
        { region: c.region, state: c.state, timezone: c.timezone },
      ]),
    ),
  };
}
